import { BaseModule } from "./baseModule";
import type { AxiosInstance } from "axios";
import type { ErrorHandler } from "./errorHandler";
import type {
  DashboardMetrics,
  SystemHealth,
  ActivityLog,
  CandidateProfile,
  UpdateSystemSettingsRequest,
  ExportRequest,
  ExportResponse,
  MessageResponse,
  JobApplicationDetails,
  ApplicationWithDetails,
  CreateTeamInvitationRequest,
  CreateTeamInvitationResponse,
  InvitationPreview,
  PanelAssignment,
  SubmitPanelFeedbackRequest,
  TeamInvitation,
  TeamMember,
  TeamSubject,
} from "./types";

/**
 * AdminModule - Handles all administrative operations
 *
 * This module provides comprehensive admin functionality including:
 * - Dashboard metrics and system health monitoring
 * - Admin user management
 * - Candidate profile management
 * - System settings management
 * - Data export functionality
 */
export class AdminModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Dashboard and System Health
  // ============================================================================

  /**
   * Get dashboard metrics
   * GET /api/admin/dashboard/metrics
   */
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return this.get<DashboardMetrics>("/api/admin/dashboard/metrics");
  }

  /**
   * Get system health status
   * GET /api/admin/system/health
   */
  async getSystemHealth(): Promise<SystemHealth> {
    return this.get<SystemHealth>("/api/admin/system/health");
  }

  /**
   * Get recent activity logs
   * GET /api/admin/activity
   */
  async getRecentActivity(): Promise<ActivityLog[]> {
    return this.get<ActivityLog[]>("/api/admin/activity");
  }

  async getTeamMembers(): Promise<TeamMember[]> {
    return this.get<TeamMember[]>("/api/admin/team/members");
  }

  async getTeamSubjects(): Promise<TeamSubject[]> {
    return this.get<TeamSubject[]>("/api/admin/team/subjects");
  }

  async createTeamSubject(name: string): Promise<TeamSubject> {
    return this.post<TeamSubject>("/api/admin/team/subjects", { name });
  }

  async getTeamInvitations(): Promise<TeamInvitation[]> {
    return this.get<TeamInvitation[]>("/api/admin/team/invitations");
  }

  async createTeamInvitation(
    data: CreateTeamInvitationRequest,
  ): Promise<CreateTeamInvitationResponse> {
    return this.post<CreateTeamInvitationResponse>(
      "/api/admin/team/invitations",
      data,
    );
  }

  async resendTeamInvitation(
    id: string,
  ): Promise<CreateTeamInvitationResponse> {
    return this.post<CreateTeamInvitationResponse>(
      `/api/admin/team/invitations/${id}/resend`,
    );
  }

  async revokeTeamInvitation(id: string): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/team/invitations/${id}`);
  }

  async updateTeamMember(
    id: string,
    data: Partial<
      Pick<TeamMember, "name" | "role" | "status" | "permissions">
    > & { subject_ids?: string[] },
  ): Promise<TeamMember> {
    return this.patch<TeamMember>(`/api/admin/team/members/${id}`, data);
  }

  async removeTeamMember(id: string): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/team/members/${id}`);
  }

  async getInvitationPreview(token: string): Promise<InvitationPreview> {
    return this.get<InvitationPreview>(
      `/api/public/team/invitations/${token}`,
      { skipAuth: true },
    );
  }

  async getPanelAssignments(): Promise<PanelAssignment[]> {
    return this.get<PanelAssignment[]>("/api/panel/assignments");
  }

  async submitPanelFeedback(
    assignmentId: string,
    data: SubmitPanelFeedbackRequest,
  ): Promise<PanelAssignment> {
    return this.post<PanelAssignment>(
      `/api/panel/assignments/${assignmentId}/feedback`,
      data,
    );
  }

  async getInterviewFeedback(interviewId: string): Promise<PanelAssignment[]> {
    return this.get<PanelAssignment[]>(
      `/api/admin/interviews/${interviewId}/feedback`,
    );
  }

  // ============================================================================
  // Candidate Management
  // ============================================================================

  /**
   * Get all candidates with filtering and pagination
   * GET /api/admin/candidates
   */
  async getCandidates(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<CandidateProfile[]> {
    const queryString = params ? this.buildQueryString(params) : "";
    const endpoint = queryString
      ? `/api/admin/candidates?${queryString}`
      : "/api/admin/candidates";
    return this.get<CandidateProfile[]>(endpoint);
  }

  /**
   * Get detailed candidate profile
   * GET /api/admin/candidates/:id
   */
  async getCandidateDetails(id: string): Promise<CandidateProfile> {
    return this.get<CandidateProfile>(`/api/admin/candidates/${id}`);
  }

  /**
   * Get all conversations (admin only)
   * GET /api/admin/conversations
   */
  async getAllConversations(): Promise<
    Array<{
      user_id: string;
      user_name: string;
      user_email: string;
      last_message: string;
      last_message_at: string;
      unread_count: number;
    }>
  > {
    return this.get("/api/admin/conversations");
  }

  /**
   * Get all applications for a specific job (admin only)
   * GET /api/admin/jobs/:id/applications
   */
  async getJobApplications(jobId: string): Promise<JobApplicationDetails[]> {
    return this.get<JobApplicationDetails[]>(
      `/api/admin/jobs/${jobId}/applications`,
    );
  }

  /**
   * Get all applications with candidate, job, and status history details.
   * GET /api/admin/applications
   */
  async getAdminApplications(): Promise<ApplicationWithDetails[]> {
    return this.get<ApplicationWithDetails[]>("/api/admin/applications");
  }

  // ============================================================================
  // System Settings
  // ============================================================================

  /**
   * Get all system settings
   * GET /api/admin/settings
   * Returns a simple key-value map
   */
  async getSystemSettings(): Promise<Record<string, string>> {
    return this.get<Record<string, string>>("/api/admin/settings");
  }

  /**
   * Update system settings
   * PUT /api/admin/settings
   */
  async updateSystemSettings(
    request: UpdateSystemSettingsRequest,
  ): Promise<MessageResponse> {
    return this.put<MessageResponse>("/api/admin/settings", request);
  }

  /**
   * Test service connection
   * POST /api/admin/settings/test-connection
   */
  async testServiceConnection(
    service: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(
      "/api/admin/settings/test-connection",
      { service },
    );
  }

  // ============================================================================
  // Google OAuth
  // ============================================================================

  /**
   * Get Google OAuth authorization URL
   * GET /api/admin/settings/google/auth-url
   */
  async getGoogleAuthUrl(): Promise<{
    auth_url: string;
    redirect_uri: string;
  }> {
    return this.get<{ auth_url: string; redirect_uri: string }>(
      "/api/admin/settings/google/auth-url",
    );
  }

  /**
   * Get Google connection status
   * GET /api/admin/settings/google/status
   */
  async getGoogleConnectionStatus(): Promise<{
    connected: boolean;
    connected_account: string | null;
    connection_valid: boolean;
  }> {
    return this.get("/api/admin/settings/google/status");
  }

  /**
   * Disconnect Google account
   * POST /api/admin/settings/google/disconnect
   */
  async disconnectGoogleAccount(): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.post("/api/admin/settings/google/disconnect", {});
  }

  // ============================================================================
  // Data Export
  // ============================================================================

  /**
   * Export jobs data
   * GET /api/admin/export/jobs
   */
  async exportJobs(request: ExportRequest): Promise<ExportResponse> {
    const queryString = this.buildQueryString(request);
    return this.get<ExportResponse>(`/api/admin/export/jobs?${queryString}`);
  }

  /**
   * Export applications data
   * GET /api/admin/export/applications
   */
  async exportApplications(request: ExportRequest): Promise<ExportResponse> {
    const queryString = this.buildQueryString(request);
    return this.get<ExportResponse>(
      `/api/admin/export/applications?${queryString}`,
    );
  }

  /**
   * Export candidates data
   * GET /api/admin/export/candidates
   */
  async exportCandidates(request: ExportRequest): Promise<ExportResponse> {
    const queryString = this.buildQueryString(request);
    return this.get<ExportResponse>(
      `/api/admin/export/candidates?${queryString}`,
    );
  }

  // ============================================================================
  // AI Generation
  // ============================================================================

  /**
   * Generate email content using AI
   * POST /api/admin/candidates/ai/generate-email
   */
  async generateEmail(
    request: import("./types").GenerateEmailRequest,
  ): Promise<import("./types").GenerateEmailResponse> {
    const templateToStage: Record<
      import("./types").EmailTemplate,
      import("./types").CandidateStage
    > = {
      job_offer: "Offer Extended",
      interview_invitation: "Interview Scheduled",
      rejection: "Rejected",
      follow_up: "Interview Completed",
      general_inquiry: "Applied",
    };

    const response = await this.post<import("./types").AIGenerationResponse>(
      "/api/admin/candidates/ai/generate-email",
      {
        candidate_name: request.recipient_name || "Candidate",
        job_title: request.job_title || "the role",
        company_name: request.company_name || "NIEDO",
        stage: templateToStage[request.template],
        additional_context: request.context,
      },
    );

    const generated =
      typeof response.content === "string"
        ? response.content
        : String(response.content || "");
    const lines = generated.split("\n");
    const subjectLine = lines.find((line) =>
      line.toLowerCase().startsWith("subject:"),
    );

    if (!subjectLine) {
      return { content: generated };
    }

    return {
      subject: subjectLine.replace(/^subject:\s*/i, "").trim(),
      content: lines
        .slice(lines.indexOf(subjectLine) + 1)
        .join("\n")
        .trim(),
    };
  }

  /**
   * Generate job description using AI
   * POST /api/admin/jobs/ai/generate-description
   */
  async generateJobDescription(
    request: import("./types").GenerateJobDescriptionRequest,
  ): Promise<import("./types").GenerateJobDescriptionResponse> {
    return this.post<import("./types").GenerateJobDescriptionResponse>(
      "/api/admin/jobs/ai/generate-description",
      request,
    );
  }
}
